import { ApolloError } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { useCallback } from 'react'

import { GetProjectQuery } from 'api/graphQL/queries/project.queries'
import { AllProjectResponse } from 'Models/project'
import { useHoveredItemActions } from '../HoveredItemStore'

/**
 * Interface representing the properties required for Project Data API.
 */
export interface ProjectDataApiProps {
    projectId?: string
}

/**
 * A custom hook that provides functionality to fetch and manage project data.
 *
 * @template T - The type of the props that extend `ProjectDataApiProps`.
 * @param {T} props - The properties required to initialize the hook.
 * @param {string} props.projectId - The ID of the project to fetch data for.
 * @returns {Object} An object containing the `fetchProjectData` function.
 * @returns {Function} returns.fetchProjectData - A function to fetch the project data.
 */
export const useProjectDataQuery = <T extends ProjectDataApiProps>({
    projectId = '',
}: T) => {
    const { setProjectData, setIsProjectDataLoading } = useHoveredItemActions()

    const handleFetchError = (error: ApolloError) => {
        setIsProjectDataLoading(false)
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-getProjectDataQuery'
        )
        console.error(summarizedError)
    }

    const onQueryFetchSuccess = (response: AllProjectResponse) => {
        setProjectData(response)
        setIsProjectDataLoading(false)
    }

    const [getProjectData] = useLazyQuery<AllProjectResponse>(GetProjectQuery, {
        fetchPolicy: 'no-cache',
        onCompleted: onQueryFetchSuccess,
        onError: handleFetchError,
    })

    const fetchProjectData = useCallback(async (): Promise<void> => {
        const queryVariables = {
            projectId,
        }
        setIsProjectDataLoading(true)
        return getProjectData({
            variables: queryVariables,
        })
    }, [projectId])

    return { fetchProjectData }
}
